/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, defineProperty as _defineProperty, extends as _extends } from '../../../../../_virtual/_rollupPluginBabelHelpers.js';
import classnames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';
import { ListBoxType } from './ListBoxPropTypes.js';

var _excluded = ["ariaLabel", "children", "className", "disabled", "innerRef", "type", "invalid", "invalidText", "isOpen"];
var handleOnKeyDown = function handleOnKeyDown(event) {
  if (event.keyCode === 27) {
    event.stopPropagation();
  }
};
var handleClick = function handleClick(event) {
  event.preventDefault();
  event.stopPropagation();
};

/**
 * `ListBox` is a generic container component that handles creating the
 * container class name in response to certain props.
 */
var ListBox = function ListBox(_ref) {
  var ariaLabel = _ref.ariaLabel,
    children = _ref.children,
    containerClassName = _ref.className,
    disabled = _ref.disabled,
    innerRef = _ref.innerRef,
    type = _ref.type,
    invalid = _ref.invalid,
    invalidText = _ref.invalidText,
    isOpen = _ref.isOpen,
    rest = _objectWithoutProperties(_ref, _excluded);
  var className = classnames(_defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, containerClassName, !!containerClassName), 'cds--list-box', true), 'cds--list-box--inline', type === 'inline'), 'cds--list-box--disabled', disabled), 'cds--list-box--expanded', isOpen));
  /* eslint-disable jsx-a11y/interactive-supports-focus */
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", _extends({
    role: "listbox",
    "aria-label": ariaLabel
    // eslint-disable-next-line react/jsx-props-no-spreading
  }, rest, {
    className: className,
    ref: innerRef,
    onKeyDown: handleOnKeyDown,
    onClick: handleClick,
    "data-invalid": invalid || undefined,
    "aria-invalid": invalid || undefined
  }), children), invalid ? /*#__PURE__*/React.createElement("div", {
    className: "cds--form-requirement"
  }, invalidText) : null);
};
ListBox.propTypes = {
  children: PropTypes.node,
  /**
   * Specify a class name to be applied on the containing list box node
   */
  className: PropTypes.string,
  /**
   * `innerRef` hook used for libraries like Downshift that require a reference
   * on a container node when it is not a native element
   */
  innerRef: PropTypes.func.isRequired,
  /**
   * Specify whether the ListBox is currently disabled
   */
  disabled: PropTypes.bool.isRequired,
  /**
   * Specify the "type" of the ListBox. Currently supports either `default` or
   * `inline` as an option.
   */
  type: ListBoxType,
  /**
   * Specify the "aria-label" of the ListBox.
   */
  ariaLabel: PropTypes.string,
  invalid: PropTypes.bool,
  invalidText: PropTypes.string,
  isOpen: PropTypes.bool
};
ListBox.defaultProps = {
  children: undefined,
  className: undefined,
  invalid: undefined,
  invalidText: undefined,
  isOpen: undefined,
  type: 'default',
  ariaLabel: 'Choose an item'
};
var ListBox$1 = ListBox;

export { ListBox$1 as default };
